import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Moon, Sun, Accessibility, Globe } from "lucide-react";
import AccessibilityPanel from "./AccessibilityPanel";
import { translations, type Lang, type Theme } from "../content";

export default function Header({
  lang,
  toggleLang,
  theme,
  toggleTheme,
}: {
  lang: Lang;
  toggleLang: () => void;
  theme: Theme;
  toggleTheme: () => void;
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [a11yOpen, setA11yOpen] = useState(false);
  const nav = translations[lang].nav;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { href: "#about", label: nav.about },
    { href: "#services", label: nav.services },
    { href: "#gallery", label: nav.gallery },
    { href: "#contact", label: nav.contact },
  ];

  return (
    <nav
      dir={lang === "he" ? "rtl" : "ltr"}
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background/80 backdrop-blur-xl py-3 border-b border-border/10"
          : "bg-transparent py-6"
      }`}
    >
      <div className="container mx-auto px-6 md:px-10 flex items-center justify-between">
        <a href="#" className="flex items-center group">
          <span className="text-xl md:text-2xl font-display font-extrabold text-foreground tracking-[-0.02em] group-hover:text-primary transition-colors">
            STY <span className="text-primary">TRAVEL</span>
          </span>
        </a>

        <div className="hidden md:flex items-center gap-4">
          {links.map((l) => (
            <a
              key={l.href}
              href={l.href}
              className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
            >
              {l.label}
            </a>
          ))}

          <div className="h-4 w-px bg-border/20 mx-2" />

          <button
            onClick={toggleTheme}
            className="text-muted-foreground hover:text-foreground transition-colors p-2"
            title={theme === "dark" ? "Light Mode" : "Dark Mode"}
          >
            {theme === "dark" ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>

          <button
            onClick={() => setA11yOpen((v) => !v)}
            className="text-muted-foreground hover:text-foreground transition-colors p-2"
            title="Accessibility"
          >
            <Accessibility className="w-5 h-5" />
          </button>

          <button
            onClick={toggleLang}
            className="flex items-center gap-2 text-xs font-bold text-primary border border-primary/40 px-4 py-2 rounded-full hover:bg-primary hover:text-primary-foreground transition-all"
          >
            <Globe className="w-3 h-3" />
            {lang === "he" ? "EN" : "עב"}
          </button>
        </div>

        <button
          className="md:hidden text-muted-foreground hover:text-foreground transition-colors p-2"
          onClick={() => setMenuOpen((v) => !v)}
          aria-label="Menu"
        >
          {menuOpen ? <X /> : <Menu />}
        </button>
      </div>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden bg-background/95 backdrop-blur-xl border-b border-border/10 overflow-hidden"
          >
            <div className="flex flex-col p-6 gap-4 items-center">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  onClick={() => setMenuOpen(false)}
                  className="text-lg font-medium text-foreground hover:text-primary transition-colors"
                >
                  {l.label}
                </a>
              ))}
              <div className="h-px w-12 bg-border/15 my-2" />
              <button
                onClick={() => {
                  toggleTheme();
                  setMenuOpen(false);
                }}
                className="text-primary text-sm flex items-center gap-2"
              >
                {theme === "dark" ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
                {theme === "dark" ? "Light Mode" : "Dark Mode"}
              </button>
              <button
                onClick={() => setA11yOpen((v) => !v)}
                className="text-primary text-sm flex items-center gap-2"
              >
                <Accessibility className="w-4 h-4" />
                {lang === "he" ? "נגישות" : "Accessibility"}
              </button>
              <button
                onClick={() => {
                  toggleLang();
                  setMenuOpen(false);
                }}
                className="text-primary text-sm flex items-center gap-2"
              >
                <Globe className="w-4 h-4" />
                {lang === "he" ? "Switch to English" : "החלף לעברית"}
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {a11yOpen && <AccessibilityPanel onClose={() => setA11yOpen(false)} lang={lang} />}
      </AnimatePresence>
    </nav>
  );
}
